import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon, FileText, Download, Eye, Send, Plus } from "lucide-react";
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentSlides, setCurrentSlides] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDateInputs, setShowDateInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceStatuses, setInvoiceStatuses] = useState({});
  const { authToken } = useContext(AuthContext);

  const authAxios = axios.create({
    baseURL: 'https://vasstrinfo.com',
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  });

  const fetchOrders = async () => {
    try {
      const response = await authAxios.get('/api/orders/');
      const ordersArray = response.data.orders || [];
      const productsArray = response.data.products || [];

      setOrders(ordersArray);
      setProducts(productsArray);

      const initialSlides = {};
      ordersArray.forEach((_, index) => {
        initialSlides[index] = 1;
      });
      setCurrentSlides(initialSlides);

      // Fetch invoice status for each delivered order
      ordersArray.forEach(order => {
        if (order.status?.delivered_order) {
          fetchInvoiceStatus(order.id);
        }
      });
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceStatus = async (orderId) => {
    try {
      const response = await authAxios.get(`/api/orders/${orderId}/invoice-status/`);
      if (response.data.success) {
        setInvoiceStatuses(prev => ({
          ...prev,
          [orderId]: response.data.data
        }));
      }
    } catch (err) {
      console.error(`Error fetching invoice status for order ${orderId}:`, err);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [authToken]);

  const plusSlides = (n, index, maxSlides = 5) => {
    const current = currentSlides[index] || 1;
    let next = current + n;
    if (next > maxSlides) next = 1;
    if (next < 1) next = maxSlides;

    setCurrentSlides(prev => ({
      ...prev,
      [index]: next,
    }));
  };

  const toggleOverlay = (order) => {
    setSelectedOrder(order);
  };

  const toggleDateInput = (orderId, field) => {
    setShowDateInputs(prev => ({
      ...prev,
      [`${orderId}-${field}`]: !prev[`${orderId}-${field}`],
    }));
  };

  const handleStatusSubmit = async (e, orderId) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const payload = {
      started_order: formData.get('started_order') === 'on',
      finished_order: formData.get('finished_order') === 'on',
      delivered_order: formData.get('delivered_order') === 'on',
    };

    if (formData.get('finished_order_time')) {
      payload.finished_order_datetime = formData.get('finished_order_time');
    }
    if (formData.get('delivered_order_time')) {
      payload.delivered_order_datetime = formData.get('delivered_order_time');
    }

    try {
      await authAxios.post(`/api/update-received/${orderId}/`, payload);
      Swal.fire({
        title: "Success!",
        text: "Order status updated!",
        icon: "success",
        confirmButtonColor: '#d25614',
      });

      // Refresh orders to get updated status
      fetchOrders();
    } catch (err) {
      console.error('Error updating order:', err);
      Swal.fire({
        title: "Error!",
        text: "Error updating order status",
        icon: "error",
        confirmButtonColor: '#d25614',
      });
    }
  };

  const handleGenerateInvoice = async (orderId) => {
    try {
      Swal.fire({
        title: 'Generating Invoice...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await authAxios.post(`/api/orders/${orderId}/generate-invoice/`);

      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Invoice generated successfully!',
          icon: 'success',
          confirmButtonColor: '#d25614',
        });

        // Refresh invoice status
        fetchInvoiceStatus(orderId);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to generate invoice',
        icon: 'error',
        confirmButtonColor: '#d25614',
      });
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/download/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: 'Success',
        text: 'Invoice downloaded!',
        icon: 'success',
        confirmButtonColor: '#d25614',
        timer: 2000,
      });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to download invoice',
        icon: 'error',
        confirmButtonColor: '#d25614',
      });
    }
  };

  const handlePreviewInvoice = async (invoiceId) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/preview/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing invoice:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to preview invoice',
        icon: 'error',
        confirmButtonColor: '#d25614',
      });
    }
  };

  const handleSendInvoiceEmail = async (invoiceId, invoiceNumber) => {
    const result = await Swal.fire({
      title: 'Send Invoice?',
      text: `Send invoice ${invoiceNumber} to customer via email?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d25614',
      cancelButtonColor: '#666',
      confirmButtonText: 'Yes, send',
    });

    if (result.isConfirmed) {
      try {
        const response = await authAxios.post(`/api/invoices/${invoiceId}/send-email/`);
        if (response.data.success) {
          Swal.fire({
            title: 'Sent!',
            text: response.data.message,
            icon: 'success',
            confirmButtonColor: '#d25614',
          });
        }
      } catch (error) {
        console.error('Error sending invoice:', error);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to send email',
          icon: 'error',
          confirmButtonColor: '#d25614',
        });
      }
    }
  };

  const handleInvoiceActions = (orderId, invoiceData) => {
    if (!invoiceData) {
      // No invoice exists - generate it
      handleGenerateInvoice(orderId);
    } else {
      // Invoice exists - show options
      Swal.fire({
        title: `Invoice ${invoiceData.invoice.invoice_number}`,
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Date:</strong> ${new Date(invoiceData.invoice.invoice_date).toLocaleDateString()}</p>
            <p><strong>Amount:</strong> ₹${parseFloat(invoiceData.invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            <p><strong>Status:</strong> ${invoiceData.invoice.is_sent ? '✓ Sent to customer' : 'Not sent'}</p>
          </div>
        `,
        icon: 'info',
        showCancelButton: true,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonColor: '#d25614',
        denyButtonColor: '#666',
        cancelButtonColor: '#999',
        confirmButtonText: '<i class="fas fa-download"></i> Download',
        denyButtonText: '<i class="fas fa-eye"></i> Preview',
        cancelButtonText: '<i class="fas fa-envelope"></i> Send Email',
      }).then((result) => {
        if (result.isConfirmed) {
          handleDownloadInvoice(invoiceData.invoice.id, invoiceData.invoice.invoice_number);
        } else if (result.isDenied) {
          handlePreviewInvoice(invoiceData.invoice.id);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          handleSendInvoiceEmail(invoiceData.invoice.id, invoiceData.invoice.invoice_number);
        }
      });
    }
  };



  if (error) return <div className="text-center text-lg text-red-600">Error: {error}</div>;

  return (
    <Base
      Content={
        <div className="min-h-screen bg-white font-sans">
          <div className="text-center py-6">
            <h1 className="text-2xl md:text-3xl font-bold text-[#d45614] mb-2">
              Orders Received From Customers
            </h1>
            <div className="flex items-center justify-center my-4">
              <div className="flex-1 h-px bg-black"></div>
              <div className="flex mx-2 md:mx-4">
                {[...Array(7)].map((_, i) => (
                  <div key={i} className="w-3 h-3 bg-black rounded-full mx-1"></div>
                ))}
              </div>
              <div className="flex-1 h-px bg-black"></div>
            </div>
          </div>

          {orders.length === 0 ? (
            <p className="text-center text-lg">No orders found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4 md:px-8 pb-12">
              {orders.map((order, index) => {
                const product = products.find(p => p.id === order.productid);
                const images = [
                  order.productimg1,
                  product?.img1,
                  product?.img2,
                  product?.img3,
                  product?.img4,
                  product?.img5
                ].filter(Boolean);

                const invoiceStatus = invoiceStatuses[order.id];
                const isDelivered = order.status?.delivered_order;

                return (
                  <div key={order.id} className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                    {/* Portrait Image Container */}
                    <div className="relative w-full aspect-[3/4] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                      {images.map((src, i) => (
                        <div
                          key={i}
                          className={`absolute inset-0 transition-opacity duration-500 ${currentSlides[index] === i + 1 ? 'opacity-100' : 'opacity-0'}`}
                        >
                          <img
                            src={src}
                            alt={`Product ${i + 1}`}
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                      ))}

                      {/* Image Navigation Arrows */}
                      {images.length > 1 && (
                        <>
                          <button
                            onClick={() => plusSlides(-1, index, images.length)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-9 h-9 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center font-bold hover:scale-110"
                          >
                            ‹
                          </button>
                          <button
                            onClick={() => plusSlides(1, index, images.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-9 h-9 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center font-bold hover:scale-110"
                          >
                            ›
                          </button>
                        </>
                      )}

                      {/* Image Counter */}
                      {images.length > 1 && (
                        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                          {currentSlides[index] || 1} / {images.length}
                        </div>
                      )}

                      {/* Invoice Badge */}
                      {isDelivered && invoiceStatus?.has_invoice && (
                        <div className="absolute top-3 left-3 bg-green-600 text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-lg flex items-center gap-1">
                          <FileText size={14} />
                          Invoice Ready
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="p-5">
                      <form onSubmit={(e) => handleStatusSubmit(e, order.order_id)} className="space-y-4">
                        {/* Order ID Header */}
                        <div className="border-b border-gray-200 pb-3">
                          <h3 className="text-base font-bold text-gray-800 truncate">
                            Order #{order.order_id}
                          </h3>
                          {order.created_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(order.created_at).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>

                        {/* Status Checkboxes */}
                        <div className="space-y-3">
                          {['started', 'finished', 'delivered'].map((stage) => (
                            <div key={stage} className="bg-gray-50 rounded-lg p-3 transition-all hover:bg-gray-100">
                              <div className="flex items-start space-x-3">
                                <input
                                  type="checkbox"
                                  id={`${stage}Order${order.id}`}
                                  name={`${stage}_order`}
                                  defaultChecked={!!order.status[`${stage}_order`]}
                                  className="w-5 h-5 mt-0.5 text-[#d45614] rounded focus:ring-2 focus:ring-orange-500 cursor-pointer"
                                />
                                <div className="flex-1">
                                  <label
                                    htmlFor={`${stage}Order${order.id}`}
                                    className="text-sm font-semibold text-gray-700 cursor-pointer block"
                                  >
                                    {stage.charAt(0).toUpperCase() + stage.slice(1)} Order
                                  </label>
                                  {order.status[`${stage}_order`] && (
                                    <span className="text-xs text-green-600 font-medium mt-1 block">
                                      ✓ {new Date(order.status[`${stage}_order`]).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  )}
                                  {showDateInputs[`${order.id}-${stage}`] && (
                                    <input
                                      type="datetime-local"
                                      className="block mt-2 w-full text-sm border rounded px-2 py-1"
                                      name={`${stage}_order_time`}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 pt-2">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                              onClick={() => toggleOverlay(order)}
                            >
                              View Details
                            </button>
                            <button
                              type="submit"
                              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            >
                              Update Status
                            </button>
                          </div>

                          {/* Invoice Button */}
                          {isDelivered && (
                            <button
                              type="button"
                              onClick={() => handleInvoiceActions(order.id, invoiceStatus)}
                              className={`w-full px-4 py-2.5 ${
                                invoiceStatus?.has_invoice
                                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                              } text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2`}
                            >
                              {invoiceStatus?.has_invoice ? (
                                <>
                                  <FileText size={16} />
                                  Invoice Options
                                </>
                              ) : (
                                <>
                                  <Plus size={16} />
                                  Generate Invoice
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                );
              })}

              {selectedOrder && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-2xl font-bold text-[#d45614]">{selectedOrder.productname}</h3>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="text-3xl text-gray-500 hover:text-gray-700 transition-colors"
                        aria-label="Close modal"
                      >
                        &times;
                      </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-col lg:flex-row gap-8">
                      {/* Image Gallery - Left Side */}
                      <div className="lg:w-2/5 xl:w-1/3">
                        {(() => {
                          const product = products.find(p => p.id === selectedOrder.productid);
                          const images = [
                            selectedOrder.productimg1,
                            product?.img1,
                            product?.img2,
                            product?.img3,
                            product?.img4,
                            product?.img5
                          ].filter(Boolean);
                          const overlayIndex = orders.findIndex(o => o.id === selectedOrder.id);

                          return (
                            <div className="relative">
                              <div className="relative w-full aspect-[3/4] mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg">
                                {images.length > 0 ? (
                                  <>
                                    {images.map((src, i) => (
                                      <div
                                        key={i}
                                        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${currentSlides[overlayIndex] === i + 1 ? 'opacity-100' : 'opacity-0'}`}
                                      >
                                        <img
                                          src={src}
                                          alt={`Product ${i + 1}`}
                                          className="w-full h-full object-contain p-3"
                                        />
                                      </div>
                                    ))}

                                    {images.length > 1 && (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            plusSlides(-1, overlayIndex, images.length);
                                          }}
                                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-10 h-10 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center font-bold text-xl hover:scale-110"
                                        >
                                          ‹
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            plusSlides(1, overlayIndex, images.length);
                                          }}
                                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-10 h-10 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center font-bold text-xl hover:scale-110"
                                        >
                                          ›
                                        </button>

                                        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1.5 rounded-full font-medium shadow-lg">
                                          {currentSlides[overlayIndex] || 1} / {images.length}
                                        </div>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-gray-400 flex items-center justify-center h-full">No images available</div>
                                )}
                              </div>

                              {images.length > 1 && (
                                <div className="flex justify-center gap-2 mt-2">
                                  {images.map((_, i) => (
                                    <button
                                      key={i}
                                      onClick={() => setCurrentSlides(prev => {
                                        const newSlides = {...prev};
                                        newSlides[overlayIndex] = i + 1;
                                        return newSlides;
                                      })}
                                      className={`w-3 h-3 rounded-full transition-colors ${currentSlides[overlayIndex] === i + 1 ? 'bg-orange-500' : 'bg-gray-300'}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Order Details - Right Side */}
                      <div className="lg:w-3/5 xl:w-2/3">
                        {/* User Information Section */}
                        <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg">
                          <h4 className="text-lg font-bold text-[#d45614] mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            User Info
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {selectedOrder.name && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Name</p>
                                <p className="font-semibold text-gray-900">{selectedOrder.name}</p>
                              </div>
                            )}
                            {selectedOrder.phonenumber && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Phone Number</p>
                                <p className="font-medium text-gray-900">{selectedOrder.phonenumber}</p>
                              </div>
                            )}
                            {selectedOrder.address && (
                              <div className="bg-white p-3 rounded-lg md:col-span-2">
                                <p className="text-xs text-gray-500 mb-1">Address</p>
                                <p className="font-medium text-gray-900">{selectedOrder.address}</p>
                              </div>
                            )}
                            {selectedOrder.address2 && (
                              <div className="bg-white p-3 rounded-lg md:col-span-2">
                                <p className="text-xs text-gray-500 mb-1">Address Line 2</p>
                                <p className="font-medium text-gray-900">{selectedOrder.address2}</p>
                              </div>
                            )}
                            {selectedOrder.city && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">City</p>
                                <p className="font-medium text-gray-900">{selectedOrder.city}</p>
                              </div>
                            )}
                            {selectedOrder.state && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">State</p>
                                <p className="font-medium text-gray-900">{selectedOrder.state}</p>
                              </div>
                            )}
                            {selectedOrder.pincode && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Pincode</p>
                                <p className="font-medium text-gray-900">{selectedOrder.pincode}</p>
                              </div>
                            )}
                            {selectedOrder.country && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Country</p>
                                <p className="font-medium text-gray-900">{selectedOrder.country}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Basic Order Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Order ID</p>
                            <p className="font-medium text-gray-900">{selectedOrder.order_id}</p>
                          </div>
                          {selectedOrder.created_at && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-sm text-gray-500 mb-1">Order Date</p>
                              <p className="font-medium text-gray-900">
                                {new Date(selectedOrder.created_at).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          )}
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Quantity</p>
                            <p className="font-medium text-gray-900">{selectedOrder.quantity}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Fabric</p>
                            <p className="font-medium text-gray-900">{selectedOrder.fabric}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Color</p>
                            <p className="font-medium text-gray-900">{selectedOrder.color}</p>
                          </div>
                          <div className="md:col-span-2 bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Measurements</p>
                            <p className="font-medium text-gray-900">
                              {selectedOrder.size || selectedOrder.measurements || 'Not specified'}
                            </p>
                          </div>
                        </div>

                        {/* Additional Notes */}
                        {(selectedOrder.description || selectedOrder.note) && (
                          <div className="mb-6">
                            {selectedOrder.description && (
                              <div className="mb-4 bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-500 mb-1">Description</p>
                                <p className="font-medium text-gray-900">{selectedOrder.description}</p>
                              </div>
                            )}
                            {selectedOrder.note && (
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-500 mb-1">Note</p>
                                <p className="font-medium text-gray-900">{selectedOrder.note}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Customizations Section */}
                        {(selectedOrder.fab !== 'N/A' || selectedOrder.border !== 'N/A' || selectedOrder.borderdesign !== 'N/A') && (
                          <div className="border-t pt-6 mb-6">
                            <h4 className="text-lg font-semibold mb-4 text-gray-800">Customizations</h4>
                            <div className="space-y-4">
                              {selectedOrder.fab && selectedOrder.fab !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Custom Fabric</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.fab_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.fab_img}
                                          alt="Custom fabric"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.fab}</p>
                                  </div>
                                </div>
                              )}

                              {selectedOrder.border && selectedOrder.border !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Custom Border</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.border_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.border_img}
                                          alt="Custom border"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.border}</p>
                                  </div>
                                </div>
                              )}

                              {selectedOrder.borderdesign && selectedOrder.borderdesign !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Border Design</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.border_design_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.border_design_img}
                                          alt="Border design"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.borderdesign}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Invoice Section */}
                        {selectedOrder.status?.delivered_order && (
                          <div className="border-t pt-6 mb-6">
                            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-4">
                              <h4 className="text-lg font-bold text-purple-700 mb-3 flex items-center">
                                <FileText className="w-5 h-5 mr-2" />
                                Invoice
                              </h4>

                              {(() => {
                                const invoiceData = invoiceStatuses[selectedOrder.id];

                                if (invoiceData?.has_invoice) {
                                  return (
                                    <div className="bg-white rounded-lg p-4 border border-purple-300">
                                      <p className="text-purple-700 font-semibold mb-2 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Invoice Generated
                                      </p>
                                      <div className="mt-2 text-sm text-gray-700 space-y-1 mb-4">
                                        <p><strong>Invoice #:</strong> {invoiceData.invoice.invoice_number}</p>
                                        <p><strong>Amount:</strong> ₹{parseFloat(invoiceData.invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                        <p><strong>Status:</strong> {invoiceData.invoice.is_sent ? <span className="text-green-600">✓ Sent to customer</span> : <span className="text-gray-600">Not sent</span>}</p>
                                      </div>

                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          onClick={() => handlePreviewInvoice(invoiceData.invoice.id)}
                                          className="flex-1 min-w-[120px] px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                                        >
                                          <Eye size={16} />
                                          Preview
                                        </button>
                                        <button
                                          onClick={() => handleDownloadInvoice(invoiceData.invoice.id, invoiceData.invoice.invoice_number)}
                                          className="flex-1 min-w-[120px] px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                                        >
                                          <Download size={16} />
                                          Download
                                        </button>
                                        <button
                                          onClick={() => handleSendInvoiceEmail(invoiceData.invoice.id, invoiceData.invoice.invoice_number)}
                                          className="flex-1 min-w-[120px] px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                                        >
                                          <Send size={16} />
                                          Send Email
                                        </button>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <button
                                      onClick={() => handleGenerateInvoice(selectedOrder.id)}
                                      className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
                                    >
                                      <Plus size={20} />
                                      Generate Invoice
                                    </button>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default OrdersPage;
